/* eslint-disable react/jsx-no-bind */
import { useEffect } from 'react'

import { Button, Input, Label, ListBox, Modal, Select } from '@heroui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { z } from 'zod'

import { Card, CardContent } from '@/components/ui/Card'
import { useUsersStore } from '@/store/users.store'
import type { Role } from '@/types/user'

const userSchema = z.object({
  name: z.string().min(2, 'Минимум 2 символа').max(64, 'Максимум 64 символа'),
  email: z.string().email('Некорректный email').min(5, 'Минимум 5 символов').max(254, 'Максимум 254 символа'),
  role: z.enum(['admin', 'accountant', 'warehouse', 'user']),
})

type FormValues = z.infer<typeof userSchema>;

interface UserFormProps {
  userId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function UserForm({ userId: propId, onSuccess, onCancel }: UserFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id: routeId } = useParams<{ id: string }>()
  const id = propId ?? routeId
  const isEdit = Boolean(id)

  const { users, addUser, updateUser } = useUsersStore()
  const existing = id ? users.find(u => u.id === id) : undefined

  const { register, handleSubmit, control, reset, formState: { errors, isSubmitting } } = useForm<FormValues>({
    resolver: zodResolver(userSchema),
    defaultValues: existing
      ? {
        name: existing.name,
        email: existing.email,
        role: existing.role,
      }
      : { name: '', email: '', role: 'accountant' },
  })

  useEffect(() => {
    if (existing) {
      reset({
        name: existing.name,
        email: existing.email,
        role: existing.role,
      })
    } else {
      reset({ name: '', email: '', role: 'accountant' })
    }
  }, [existing, reset])

  const onSubmit = async (data: FormValues) => {
    const payload = {
      ...data,
      name: data.name.toUpperCase(),
      email: data.email.toLowerCase(),
      phone: existing?.phone || '',
      role: data.role as Role,
    }

    try {
      if (isEdit && id) {
        await updateUser(id, payload)
      } else {
        await addUser(payload)
      }

      if (onSuccess) {
        onSuccess()
      } else {
        navigate('/users')
      }
    } catch (err) {
      console.error('Failed to save user:', err)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/users')
    }
  }

  const formContent = (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-1">
      <div className="flex flex-col gap-1">
        <Label htmlFor="user-name" isRequired>{t('users.full_name')}</Label>
        <Input
          id="user-name"
          placeholder="ИВАНОВ ИВАН"
          className={errors.name ? 'border-danger' : ''}
          {...register('name')}
          variant={'secondary'}
        />
        {errors.name?.message && <span className="text-xs text-danger">{errors.name.message}</span>}
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="user-email" isRequired>{t('users.email')}</Label>
        <Input
          id="user-email"
          type="email"
          placeholder="example@westcontinent.com"
          className={errors.email ? 'border-danger' : ''}
          {...register('email')}
          variant={'secondary'}
          disabled={isEdit}
        />
        {errors.email?.message && <span className="text-xs text-danger">{errors.email.message}</span>}
      </div>


      <Controller
        name="role"
        control={control}
        render={({ field }) => (
          <div className="flex flex-col gap-1">
            <Label isRequired>{t('users.role')}</Label>
            <Select
              className="w-full"
              selectedKey={field.value}
              onSelectionChange={key => field.onChange(key as string)}
              variant={'secondary'}
            >
              <Select.Trigger ref={field.ref}>
                <Select.Value>
                  {({ selectedText }) => selectedText}
                </Select.Value>
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover className="min-w-[200px] p-1 bg-background border rounded-xl shadow-lg">
                <ListBox>
                  <ListBox.Item id="admin" textValue={t('users.roles.admin')}>
                    {t('users.roles.admin')}
                  </ListBox.Item>
                  <ListBox.Item id="accountant" textValue={t('users.roles.accountant')}>
                    {t('users.roles.accountant')}
                  </ListBox.Item>
                  <ListBox.Item id="warehouse" textValue={t('users.roles.warehouse')}>
                    {t('users.roles.warehouse')}
                  </ListBox.Item>
                </ListBox>
              </Select.Popover>
            </Select>
            {errors.role?.message && <span className="text-xs text-danger">{errors.role.message}</span>}
          </div>
        )}
      />

      <div className="flex gap-3 pt-4 justify-end">
        <Button variant="ghost" onClick={handleCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" isDisabled={isSubmitting}>
          {isSubmitting ? (
            <div className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {isEdit ? t('common.save') : t('users.add_user')}
            </div>
          ) : (
            isEdit ? t('common.save') : t('users.add_user')
          )}
        </Button>
      </div>
    </form>
  )

  if (onSuccess) {
    return formContent
  }

  return (
    <div className="p-6 max-w-lg space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={handleCancel}
          className="p-1.5 rounded-lg transition-colors hover:bg-zinc-100 text-zinc-500 dark:hover:bg-zinc-800 dark:text-zinc-400"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div>
          <h1 className="text-xl font-semibold text-zinc-800 dark:text-zinc-100">
            {isEdit ? t('users.edit_user') : t('users.new_user')}
          </h1>
        </div>
      </div>

      <Card>
        <CardContent>
          {formContent}
        </CardContent>
      </Card>
    </div>
  )
}

interface UserDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  userId?: string;
}

export default function UserDialog({ isOpen, onOpenChange, userId }: UserDialogProps) {
  const { t } = useTranslation()

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Backdrop variant="blur" isDismissable>
        <Modal.Container>
          <Modal.Dialog>
            <Modal.CloseTrigger />
            <Modal.Header>
              <Modal.Heading className={'font-bold'}>
                {userId ? t('users.edit_user') : t('users.new_user')}
              </Modal.Heading>
            </Modal.Header>
            <Modal.Body className={'mt-4'}>
              <UserForm
                key={userId ?? 'new'}
                userId={userId}
                onSuccess={() => onOpenChange(false)}
                onCancel={() => onOpenChange(false)}
              />
            </Modal.Body>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  )
}
